import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { Snackbar, Alert } from '@mui/material';
import { DARK_TEXT } from '../utils/brandColors';

const ToastContext = createContext(null);

export const useToast = () => useContext(ToastContext);

export default function ToastProvider({ children }) {
  const [toast, setToast] = useState({ open: false, message: '', severity: 'info' });

  const showToast = useCallback((message, severity = 'info') => {
    setToast({ open: true, message, severity });
  }, []);

  const hideToast = useCallback(() => {
    setToast((prev) => ({ ...prev, open: false }));
  }, []);

  // Surface connectivity changes app-wide. Without this, a dropped network
  // makes every action fail silently or as a raw error. WsContext /
  // useImageRetry consume the same events for silent reconnect plumbing —
  // this is the only user-facing signal.
  const wasOffline = useRef(false);
  useEffect(() => {
    const handleOffline = () => {
      wasOffline.current = true;
      showToast("You're offline — actions won't save", 'warning');
    };
    const handleOnline = () => {
      // Only announce recovery after we told the user they were offline;
      // avoids a spurious "Back online" on blips the user never saw.
      if (!wasOffline.current) return;
      wasOffline.current = false;
      showToast('Back online', 'success');
    };
    if (typeof navigator !== 'undefined' && 'onLine' in navigator && !navigator.onLine) {
      handleOffline();
    }
    window.addEventListener('offline', handleOffline);
    window.addEventListener('online', handleOnline);
    return () => {
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('online', handleOnline);
    };
  }, [showToast]);

  return (
    <ToastContext.Provider value={showToast}>
      {children}
      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={hideToast}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={{ mb: { xs: 8, sm: 2 } }}
      >
        <Alert
          onClose={hideToast}
          severity={toast.severity}
          variant="filled"
          sx={{
            width: '100%',
            borderRadius: 2,
            fontWeight: 600,
            // Info toasts render neutral near-black instead of MUI's bright
            // blue; success/error/warning keep their semantic colors.
            ...(toast.severity === 'info' && {
              bgcolor: DARK_TEXT,
              color: '#fff',
              '& .MuiAlert-icon': { color: '#fff' },
            }),
          }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </ToastContext.Provider>
  );
}
